// Matériaux : construction des MaterialRequest (contrat avec src/render) et matériau par défaut.
import * as THREE from 'three';
import type { MaterialKind, MaterialRequest } from './types';
import type { MaterialDef } from './format';

/** Requête de matériau à partir d'un .mhmat converti ; les textures sont résolues en URL absolues. */
export function materialRequest(kind: MaterialKind, assetId: string, def: MaterialDef | null, assetUrl: string): MaterialRequest {
  const d: MaterialDef = def ?? {
    name: null, diffuseColor: [1, 1, 1], specularColor: [0.1, 0.1, 0.1], shininess: 0.3, opacity: 1, translucency: 0,
    transparent: false, alphaToCoverage: true, backfaceCull: true, castShadows: true, receiveShadows: true,
    textures: {}, extra: {},
  };
  const textures: MaterialRequest['textures'] = {};
  for (const k of ['diffuse', 'normal', 'specular', 'bump', 'transparency', 'ao'] as const) {
    const t = d.textures[k];
    if (t) textures[k] = new URL(t, assetUrl).href;
  }
  const extra: MaterialRequest['extra'] = {
    shininess: d.shininess, opacity: d.opacity, translucency: d.translucency,
    specularColor: d.specularColor.join(' '), castShadows: d.castShadows, receiveShadows: d.receiveShadows,
  };
  if (d.textures.displacement) extra.displacementTexture = new URL(d.textures.displacement, assetUrl).href;
  for (const [k, v] of Object.entries(d.extra)) extra[k] = Array.isArray(v) ? v.join(' ') : v;
  return {
    kind, assetId, textures,
    diffuseColor: [d.diffuseColor[0], d.diffuseColor[1], d.diffuseColor[2]],
    transparent: d.transparent, alphaToCoverage: d.alphaToCoverage, backfaceCull: d.backfaceCull, extra,
  };
}

/** Cache de textures partagé (une texture par URL et espace colorimétrique). */
const texCache = new Map<string, { tex: THREE.Texture; refs: number }>();
const loader = new THREE.TextureLoader();

export function loadTexture(url: string, srgb: boolean): THREE.Texture {
  const key = url + (srgb ? '#srgb' : '#lin');
  const hit = texCache.get(key);
  if (hit) {
    hit.refs++;
    return hit.tex;
  }
  const tex = loader.load(url);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.anisotropy = 4;
  texCache.set(key, { tex, refs: 1 });
  return tex;
}

export function releaseTexture(tex: THREE.Texture) {
  for (const [k, e] of texCache) {
    if (e.tex === tex) {
      if (--e.refs <= 0) {
        tex.dispose();
        texCache.delete(k);
      }
      return;
    }
  }
}

/** Libère les textures d'un matériau créé par defaultMaterial(). */
export function disposeDefaultMaterial(mat: THREE.Material) {
  const m = mat as THREE.MeshStandardMaterial;
  for (const t of [m.map, m.normalMap, m.roughnessMap, m.aoMap, m.alphaMap, m.bumpMap]) if (t) releaseTexture(t);
  mat.dispose();
}

/**
 * Matériau par défaut (MeshStandardMaterial) quand aucun materialProvider n'est fourni.
 * Cheveux / sourcils / cils : alpha test + alpha-to-coverage (nécessite le MSAA pour être doux).
 */
export function defaultMaterial(req: MaterialRequest): THREE.Material {
  const shin = Number(req.extra.shininess ?? 0.3);
  const m = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setRGB(req.diffuseColor[0], req.diffuseColor[1], req.diffuseColor[2], THREE.SRGBColorSpace),
    roughness: THREE.MathUtils.clamp(1 - 0.55 * shin, 0.25, 1),
    metalness: 0,
    side: req.backfaceCull ? THREE.FrontSide : THREE.DoubleSide,
  });
  m.name = `${req.kind}:${req.assetId}`;
  if (req.textures.diffuse) m.map = loadTexture(req.textures.diffuse, true);
  if (req.textures.normal) m.normalMap = loadTexture(req.textures.normal, false);
  if (req.textures.ao) m.aoMap = loadTexture(req.textures.ao, false);
  switch (req.kind) {
    case 'skin':
      m.roughness = 0.55;
      break;
    case 'hair':
    case 'eyebrows':
    case 'eyelashes':
      m.alphaTest = 0.5;
      m.alphaToCoverage = true;
      m.side = THREE.DoubleSide;
      m.roughness = 0.6;
      break;
    case 'eyes':
      // texture RGBA : iris/sclère opaques, cornée = disque alpha ≈ 0 -> alpha test
      m.alphaTest = 0.5;
      m.roughness = 0.15;
      break;
    case 'teeth':
    case 'tongue':
      m.roughness = 0.35;
      break;
    default:
      if (req.transparent) {
        m.alphaTest = 0.5;
        m.alphaToCoverage = req.alphaToCoverage;
      }
  }
  return m;
}
